import { lazy, useEffect } from 'react'
import { ConfigProvider, App as AntdApp } from 'antd'
import deDE from 'antd/locale/de_DE'
import { themeConfig } from './theme'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AdminLayout from './components/AdminLayout'
import CookieConsent from './components/CookieConsent'
import { applyBranding, useBranding } from './stores/branding'
import SetupPage from './pages/Setup'
import LoginPage from './pages/Login'
import LandingPage from './pages/Landing'

// Frequently-visited pages stay eagerly imported (dashboard + main
// list-pages) so they don't trigger an extra network round-trip.
import DashboardPage from './pages/Admin/Dashboard'
import CustomersPage from './pages/Admin/Customers'
import Subscriptions from './pages/Admin/Subscriptions'
import Invoices from './pages/Admin/Invoices'
import Payments from './pages/Admin/Payments'
import Orders from './pages/Admin/Orders'

// Heavy/rarely-visited pages get split-bundled via lazy() so the
// initial JS payload stays under ~500 KB gzip. The Suspense fallback
// is a small spinner so the user sees something during the chunk
// fetch (typically <300 ms over LAN).
const CustomerDetail = lazy(() => import('./pages/Admin/CustomerDetail'))
const NewCustomerPage = lazy(() => import('./pages/Admin/NewCustomerPage'))
const SepaMandates = lazy(() => import('./pages/Admin/SepaMandates'))
const CamtImport = lazy(() => import('./pages/Admin/CamtImport'))
const Banking = lazy(() => import('./pages/Admin/Banking'))
const Backups = lazy(() => import('./pages/Admin/Backups'))
const ProvisioningJobs = lazy(() => import('./pages/Admin/ProvisioningJobs'))
const ServiceInstances = lazy(() => import('./pages/Admin/ServiceInstances'))
const ProvisioningServers = lazy(() => import('./pages/Admin/ProvisioningServers'))
const TicketMailboxes = lazy(() => import('./pages/Admin/TicketMailboxes'))
const Settings = lazy(() => import('./pages/Admin/Settings'))
const Team = lazy(() => import('./pages/Admin/Team'))
const PortalUsersPage = lazy(() => import('./pages/Admin/PortalUsers'))
const Quotes = lazy(() => import('./pages/Admin/Quotes'))
const CreditNotes = lazy(() => import('./pages/Admin/CreditNotes'))
const Coupons = lazy(() => import('./pages/Admin/Coupons'))
const MassMail = lazy(() => import('./pages/Admin/MassMail'))
const Newsletter = lazy(() => import('./pages/Admin/Newsletter'))
const Domains = lazy(() => import('./pages/Admin/Domains'))
const SslCertificates = lazy(() => import('./pages/Admin/SslCertificates'))
const IPManagement = lazy(() => import('./pages/Admin/IPManagement'))
const Datacenter = lazy(() => import('./pages/Admin/Datacenter'))
const ServerManagement = lazy(() => import('./pages/Admin/ServerManagement'))
const HostDetailPage = lazy(() => import('./pages/Admin/HostDetailPage'))
const Monitoring = lazy(() => import('./pages/Admin/Monitoring'))
const MonitoringDashboard = lazy(() => import('./pages/Admin/MonitoringDashboard'))
const MonitoringSatellites = lazy(() => import('./pages/Admin/MonitoringSatellites'))
const MonitoringSatelliteDetail = lazy(() => import('./pages/Admin/MonitoringSatelliteDetail'))
const MonitoringHosts = lazy(() => import('./pages/Admin/MonitoringHosts'))
const MonitoringHostDetail = lazy(() => import('./pages/Admin/MonitoringHostDetail'))
const MonitoringAlerts = lazy(() => import('./pages/Admin/MonitoringAlerts'))
const MonitoringHeartbeats = lazy(() => import('./pages/Admin/MonitoringHeartbeats'))
const MonitoringChannels = lazy(() => import('./pages/Admin/MonitoringChannels'))
const MonitoringConfig = lazy(() => import('./pages/Admin/MonitoringConfig'))
const UsageRecordsPage = lazy(() => import('./pages/Admin/UsageRecords'))
const Contracts = lazy(() => import('./pages/Admin/Contracts'))
const Licenses = lazy(() => import('./pages/Admin/Licenses'))
const AdminReseller = lazy(() => import('./pages/Admin/Reseller'))
const LicenseServer = lazy(() => import('./pages/Admin/LicenseServer'))
const Affiliates = lazy(() => import('./pages/Admin/Affiliates'))
const Tasks = lazy(() => import('./pages/Admin/Tasks'))
const Reports = lazy(() => import('./pages/Admin/Reports'))
const KnowledgeBase = lazy(() => import('./pages/Admin/KnowledgeBase'))
const Announcements = lazy(() => import('./pages/Admin/Announcements'))
const StatusPageAdmin = lazy(() => import('./pages/Admin/StatusPage'))
const TicketDepartments = lazy(() => import('./pages/Admin/TicketDepartments'))
const PredefinedReplies = lazy(() => import('./pages/Admin/PredefinedReplies'))
const SLAPolicies = lazy(() => import('./pages/Admin/SLAPolicies'))
const CancelRequests = lazy(() => import('./pages/Admin/CancelRequests'))
const TerminateReviews = lazy(() => import('./pages/Admin/TerminateReviews'))
const DunningDatev = lazy(() => import('./pages/Admin/DunningDatev'))
const Ledger = lazy(() => import('./pages/Admin/Ledger'))
const Account = lazy(() => import('./pages/Admin/Account'))
const AuditLog = lazy(() => import('./pages/Admin/AuditLog'))
const ProductsPage = lazy(() => import('./pages/Admin/Products'))
const ProductDetail = lazy(() => import('./pages/Admin/ProductDetail'))
const DSGVOPage = lazy(() => import('./pages/Admin/DSGVO'))
const ExtensionsPage = lazy(() => import('./pages/Admin/Extensions'))
const TicketsPage = lazy(() => import('./pages/Admin/Tickets'))
const DSGVORequestsPage = lazy(() => import('./pages/Admin/DSGVORequests'))
const InvoiceDetailPage = lazy(() => import('./pages/Admin/InvoiceDetailPage'))
const SubscriptionDetailPage = lazy(() => import('./pages/Admin/SubscriptionDetailPage'))
const QuoteDetailPage = lazy(() => import('./pages/Admin/QuoteDetailPage'))
const OrderDetailPage = lazy(() => import('./pages/Admin/OrderDetailPage'))
const TicketDetailPage = lazy(() => import('./pages/Admin/TicketDetailPage'))
const MailLog = lazy(() => import('./pages/Admin/MailLog'))
const ClassicImport = lazy(() => import('./pages/Admin/ClassicImport'))
const CatalogSettingsPage = lazy(() => import('./pages/Admin/CatalogSettings'))
const BillingAutomationPage = lazy(() => import('./pages/Admin/BillingAutomation'))
const WebhooksPage = lazy(() => import('./pages/Admin/Webhooks'))
const AdminNotificationsPage = lazy(() => import('./pages/Admin/Notifications'))
const ExpensesPage = lazy(() => import('./pages/Admin/Expenses'))
const DNSPage = lazy(() => import('./pages/Admin/DNS'))
const PortalDNSPage = lazy(() => import('./pages/Portal/DNS'))
const PortalApiAccess = lazy(() => import('./pages/Portal/ApiAccess'))
const PortalDomainsPage = lazy(() => import('./pages/Portal/Domains'))
const PortalSslPage = lazy(() => import('./pages/Portal/Ssl'))
const PortalServersPage = lazy(() => import('./pages/Portal/Servers'))

import PortalLayout from './pages/Portal/PortalLayout'
import PortalLoginPage from './pages/Portal/Login'
import { PasswordResetRequest, PasswordResetConfirm } from './pages/Portal/PasswordReset'
import PortalDashboard from './pages/Portal/Dashboard'
import PortalServices from './pages/Portal/Services'
const PortalServiceDetail = lazy(() => import('./pages/Portal/ServiceDetail'))
import PortalTickets from './pages/Portal/Tickets'
import PortalDatenschutz from './pages/Portal/Datenschutz'
import PortalProfile from './pages/Portal/Profile'
import PortalShop, { ShopProductPage } from './pages/Portal/Shop'
import CartPage from './pages/Portal/Cart'
import PublicShopLayout from './pages/Portal/PublicShopLayout'
const ShopDomains = lazy(() => import('./pages/Portal/ShopDomains'))
const LegalPage = lazy(() => import('./pages/Portal/LegalPage'))
const PortalAffiliate = lazy(() => import('./pages/Portal/Affiliate'))
const PortalUsage = lazy(() => import('./pages/Portal/Usage'))
const PortalIPs = lazy(() => import('./pages/Portal/IPs'))
const PortalSignup = lazy(() => import('./pages/Portal/Signup'))
const PortalVerifyEmail = lazy(() => import('./pages/Portal/VerifyEmail'))
const PortalConfirmEmailChange = lazy(() => import('./pages/Portal/ConfirmEmailChange'))
const PortalBilling = lazy(() => import('./pages/Portal/Billing'))
const PortalCancelRequests = lazy(() => import('./pages/Portal/CancelRequests'))
const PortalQuotes = lazy(() => import('./pages/Portal/Quotes'))
const PortalContracts = lazy(() => import('./pages/Portal/Contracts'))
const PortalLicenses = lazy(() => import('./pages/Portal/Licenses'))
const PortalReseller = lazy(() => import('./pages/Portal/Reseller'))
const PortalOrders = lazy(() => import('./pages/Portal/Orders'))
const PortalNotifications = lazy(() => import('./pages/Portal/Notifications'))
const PortalMonitoring = lazy(() => import('./pages/Portal/Monitoring'))
const PortalAntispam = lazy(() => import('./pages/Portal/Antispam'))
const PortalTicketDetailPage = lazy(() => import('./pages/Portal/TicketDetailPage'))
const PortalInvoiceDetailPage = lazy(() => import('./pages/Portal/InvoiceDetail'))
const PortalOrderDetailPage = lazy(() => import('./pages/Portal/OrderDetail'))
const PublicQuoteAccept = lazy(() => import('./pages/Public/QuoteAccept'))
const PublicStatus = lazy(() => import('./pages/Public/Status'))
const PublicMonStatus = lazy(() => import('./pages/Public/MonStatus'))
const PublicKB = lazy(() => import('./pages/Public/KB'))
const PublicCSAT = lazy(() => import('./pages/CSAT'))
const PublicPayInvoice = lazy(() => import('./pages/PayInvoice'))
const PublicKBArticle = lazy(() =>
  import('./pages/Public/KB').then((m) => ({ default: m.PublicKBArticle })))
import { useAuthStore } from './stores/auth'
import apiClient from './api/client'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 1,
      // Refetch when the user returns to the tab so out-of-band DB changes
      // (admin actions, provisioning jobs, another session) become visible
      // without a hard reload. staleTime keeps this from refetching on every
      // quick focus flicker.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
})


// Admin-Bereich wird auf der control-Subdomain (control.*) oder dem Legacy-
// Admin-Port :3443 (lokale Dev) ausgeliefert. Alles andere (zkm.*, sonstige
// Hosts) = Kundenportal.
const isAdminHost = () =>
  window.location.port === '3443' ||
  window.location.hostname.startsWith('control.')


function SetupGate() {
  const { i18n } = useTranslation()
  const setSetupCompleted = useAuthStore((s) => s.setSetupCompleted)
  const setupCompleted = useAuthStore((s) => s.setupCompleted)
  const branding = useBranding()

  useEffect(() => {
    apiClient
      .get<{ data: { completed: boolean } }>('/api/v1/setup/status')
      .then((res) => setSetupCompleted(res.data.data.completed))
      .catch(() => setSetupCompleted(false))
  }, [setSetupCompleted])

  useEffect(() => {
    if (!i18n.isInitialized) i18n.loadLanguages(['de', 'en'])
  }, [i18n])

  useEffect(() => {
    applyBranding(branding.data)
  }, [branding.data])

  if (setupCompleted === null) return null // brief flicker — could be a Spin

  // ── Admin-Host (control.* / :3443) ────────────────────────────────────────
  if (isAdminHost()) {
    return (
      <Routes>
        <Route
          path="/setup"
          element={setupCompleted ? <Navigate to="/login" replace /> : <SetupPage />}
        />
        <Route
          path="/login"
          element={setupCompleted ? <LoginPage /> : <Navigate to="/setup" replace />}
        />
        <Route element={<AdminLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/new" element={<NewCustomerPage />} />
          <Route path="/customers/:id" element={<CustomerDetail />} />
          <Route path="/subscriptions" element={<Subscriptions />} />
          <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
          <Route path="/invoices" element={<Invoices />} />
          <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
          <Route path="/mail-log" element={<MailLog />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/sepa-mandates" element={<SepaMandates />} />
          <Route path="/camt-import" element={<CamtImport />} />
          <Route path="/banking" element={<Banking />} />
          <Route path="/backups" element={<Backups />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/:id" element={<OrderDetailPage />} />
          <Route path="/provisioning-jobs" element={<ProvisioningJobs />} />
          <Route path="/provisioning-servers" element={<ProvisioningServers />} />
          <Route path="/service-instances" element={<ServiceInstances />} />
          <Route path="/ticket-mailboxes" element={<TicketMailboxes />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/team" element={<Team />} />
          <Route path="/portal-users" element={<PortalUsersPage />} />
          <Route path="/quotes" element={<Quotes />} />
          <Route path="/quotes/:id" element={<QuoteDetailPage />} />
          <Route path="/credit-notes" element={<CreditNotes />} />
          <Route path="/coupons" element={<Coupons />} />
          <Route path="/mass-mails" element={<MassMail />} />
          <Route path="/newsletter" element={<Newsletter />} />
          <Route path="/domains" element={<Domains />} />
          <Route path="/ssl" element={<SslCertificates />} />
          <Route path="/ip-management" element={<IPManagement />} />
          <Route path="/datacenter" element={<Datacenter />} />
          <Route path="/server-management" element={<ServerManagement />} />
          <Route path="/server-management/hosts/:id" element={<HostDetailPage />} />
          <Route path="/monitoring" element={<Monitoring />} />
          <Route path="/monitoring-v2" element={<Navigate to="/monitoring-v2/dashboard" replace />} />
          <Route path="/monitoring-v2/dashboard" element={<MonitoringDashboard />} />
          <Route path="/monitoring-v2/satellites" element={<MonitoringSatellites />} />
          <Route path="/monitoring-v2/satellites/:id" element={<MonitoringSatelliteDetail />} />
          <Route path="/monitoring-v2/hosts" element={<MonitoringHosts />} />
          <Route path="/monitoring-v2/hosts/:id" element={<MonitoringHostDetail />} />
          <Route path="/monitoring-v2/alerts" element={<MonitoringAlerts />} />
          <Route path="/monitoring-v2/heartbeats" element={<MonitoringHeartbeats />} />
          <Route path="/monitoring-v2/channels" element={<MonitoringChannels />} />
          <Route path="/monitoring-v2/config" element={<MonitoringConfig />} />
          <Route path="/usage-records" element={<UsageRecordsPage />} />
          <Route path="/contracts" element={<Contracts />} />
          <Route path="/licenses" element={<Licenses />} />
          <Route path="/reseller" element={<AdminReseller />} />
          <Route path="/license-server" element={<LicenseServer />} />
          <Route path="/dns" element={<DNSPage />} />
          <Route path="/affiliates" element={<Affiliates />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/kb" element={<KnowledgeBase />} />
          <Route path="/announcements" element={<Announcements />} />
          <Route path="/status-page" element={<StatusPageAdmin />} />
          <Route path="/ticket-departments" element={<TicketDepartments />} />
          <Route path="/predefined-replies" element={<PredefinedReplies />} />
          <Route path="/sla-policies" element={<SLAPolicies />} />
          <Route path="/cancel-requests" element={<CancelRequests />} />
          <Route path="/terminate-reviews" element={<TerminateReviews />} />
          <Route path="/catalog-settings" element={<CatalogSettingsPage />} />
          <Route path="/billing-automation" element={<BillingAutomationPage />} />
          <Route path="/system-settings" element={<Settings />} />
          <Route path="/dunning" element={<DunningDatev />} />
          <Route path="/ledger" element={<Ledger />} />
          <Route path="/account" element={<Account />} />
          <Route path="/audit-log" element={<AuditLog />} />
          <Route path="/products" element={<ProductsPage />} />
          {/* /products/new is handled by :id (id==='new' → isNew). A literal
              route here would shadow :id and leave useParams().id undefined,
              breaking the slug field + create flow. */}
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/extensions" element={<ExtensionsPage />} />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/:id" element={<TicketDetailPage />} />
          <Route path="/dsgvo" element={<DSGVOPage />} />
          <Route path="/dsgvo-requests" element={<DSGVORequestsPage />} />
          <Route path="/webhooks" element={<WebhooksPage />} />
          <Route path="/admin-notifications" element={<AdminNotificationsPage />} />
          <Route path="/classic-import" element={<ClassicImport />} />
          <Route path="/expenses" element={<ExpensesPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // ── Portal / Shop (Port 443 / alle anderen Ports) ─────────────────────────
  return (
    <Routes>
      {/* Landing-Page */}
      <Route path="/" element={<LandingPage />} />

      {/* Portal Auth */}
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route path="/portal/reset-request" element={<PasswordResetRequest />} />
      <Route path="/portal/reset" element={<PasswordResetConfirm />} />
      <Route path="/portal/signup" element={<PortalSignup />} />
      <Route path="/portal/verify-email" element={<PortalVerifyEmail />} />
      <Route path="/portal/confirm-email-change" element={<PortalConfirmEmailChange />} />

      {/* Öffentliche Seiten */}
      <Route path="/status" element={<PublicStatus />} />
      <Route path="/status/:slug" element={<PublicMonStatus />} />
      <Route path="/kb" element={<PublicKB />} />
      <Route path="/kb/:slug" element={<PublicKBArticle />} />
      <Route path="/quotes/:token" element={<PublicQuoteAccept />} />
      <Route path="/csat/:token" element={<PublicCSAT />} />
      <Route path="/pay/:token" element={<PublicPayInvoice />} />
      {/* Öffentlicher Shop + Warenkorb — eigenes Layout, kein Auth */}
      <Route element={<PublicShopLayout />}>
        <Route path="/shop" element={<PortalShop />} />
        <Route path="/shop/domains" element={<ShopDomains />} />
        <Route path="/shop/:productSlug" element={<ShopProductPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/legal/:doc" element={<LegalPage />} />
      </Route>

      {/* Portal (authentifiziert) */}
      <Route path="/portal" element={<PortalLayout />}>
        <Route index element={<PortalDashboard />} />
        {/* Konsolidierte Finanz-Seite */}
        <Route path="billing" element={<PortalBilling />} />
        {/* Redirects für alte Einzel-URLs (Lesezeichen, Dashboard-Links) */}
        <Route path="invoices" element={<Navigate to="/portal/billing?tab=invoices" replace />} />
        <Route path="invoices/:id" element={<PortalInvoiceDetailPage />} />
        <Route path="payments" element={<Navigate to="/portal/billing?tab=payments" replace />} />
        <Route path="credit-notes" element={<Navigate to="/portal/billing?tab=credit-notes" replace />} />
        <Route path="wallet" element={<Navigate to="/portal/billing?tab=wallet" replace />} />
        <Route path="payment-methods" element={<Navigate to="/portal/billing?tab=payment-methods" replace />} />
        <Route path="statement" element={<Navigate to="/portal/billing?tab=statement" replace />} />
        <Route path="services" element={<PortalServices />} />
        <Route path="services/:id" element={<PortalServiceDetail />} />
        <Route path="licenses" element={<PortalLicenses />} />
        <Route path="reseller" element={<PortalReseller />} />
        <Route path="tickets" element={<PortalTickets />} />
        <Route path="tickets/:id" element={<PortalTicketDetailPage />} />
        <Route path="domains" element={<PortalDomainsPage />} />
        <Route path="ssl" element={<PortalSslPage />} />
        <Route path="servers" element={<PortalServersPage />} />
        <Route path="dns" element={<PortalDNSPage />} />
        <Route path="api" element={<PortalApiAccess />} />
        <Route path="datenschutz" element={<PortalDatenschutz />} />
        <Route path="profile" element={<PortalProfile />} />
        <Route path="shop" element={<PortalShop />} />
        <Route path="shop/domains" element={<ShopDomains />} />
        <Route path="shop/:productSlug" element={<ShopProductPage />} />
        <Route path="cart" element={<CartPage />} />
        <Route path="affiliate" element={<PortalAffiliate />} />
        <Route path="usage" element={<PortalUsage />} />
        <Route path="ips" element={<PortalIPs />} />
        <Route path="cancellations" element={<PortalCancelRequests />} />
        <Route path="quotes" element={<PortalQuotes />} />
        <Route path="contracts" element={<PortalContracts />} />
        <Route path="orders" element={<PortalOrders />} />
        <Route path="orders/:id" element={<PortalOrderDetailPage />} />
        <Route path="notifications" element={<PortalNotifications />} />
        <Route path="monitoring" element={<PortalMonitoring />} />
        <Route path="antispam" element={<PortalAntispam />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <ConfigProvider locale={deDE} theme={themeConfig}>
      <AntdApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SetupGate />
            <CookieConsent />
          </BrowserRouter>
        </QueryClientProvider>
      </AntdApp>
    </ConfigProvider>
  )
}
